import React from "react";
import {
  makeStyles,
  Typography,
  Checkbox,
  FormControlLabel,
  TextField,
  Grid,
  Button,
  Link,
  Card,
  CardMedia,
  CardContent,
  useMediaQuery,
} from "@material-ui/core";
import { COLORS, SEASONS_ORDERED, LTMS } from "../../types";
import { clone } from "lodash";

const useStyles = makeStyles((theme) => ({
  h5prefix: {
    color: "#fff",
    fontSize: "0.8rem",
    padding: "0.25rem 0.3rem",
    verticalAlign: "middle",
    borderRadius: "2px",
    boxShadow: "0 0 4px #00000066",
  },
  h5: {
    paddingTop: "0.5rem",
    paddingBottom: "0.5rem",
    paddingLeft: "1rem",
    fontWeight: "bold",
    fontSize: "18px",
  },
  h5prefix_dark: {
    color: "#111",
    fontSize: "0.8rem",
    padding: "0.25rem 0.3rem",
    verticalAlign: "middle",
    borderRadius: "2px",
    boxShadow: "0 0 4px #00000066",
  },
  h5_dark: {
    color: "#111",
    paddingTop: "0.5rem",
    paddingBottom: "0.5rem",
    paddingLeft: "1rem",
    fontWeight: "bold",
    fontSize: "18px",
  },
  titleValue: {
    fontSize: "0.8rem",
    verticalAlign: "14%",
    fontWeight: "normal",

    [theme.breakpoints.down("sm")]: {
      fontSize: "0.7rem",
    },
  },
  divider: {
    marginBottom: "1.2rem",
  },
  card: {
    height: "100%",
  },
}));

export default ({ userData, update, setActiveTab, resetAll }) => {
  const classes = useStyles();
  const SEASONS_ORDERED_REVERSED = clone(SEASONS_ORDERED).reverse();
  const {
    accountLevel,
    accountPacks,
    seasonalPacks,
    seasonalAchievedLevel,
    seasonalBattlePasses,
    miscPacks,
    twitchPrime,
    purchasedPacks,
    seasonalQuestPacks,
    seasonalTreasurePacks,
    seasonalLTMs,
  } = userData;
  const mqBelow1000 = useMediaQuery("(max-width: 1000px)");

  return (
    <>
      <Grid
        container
        spacing={3}
        justify="space-between"
        alignItems="stretch"
        style={{
          maxWidth: !!mqBelow1000 ? "100%" : 994,
          marginLeft: "auto",
          marginRight: "auto",
        }}
      >
        {/* main account */}
        <Grid item xs={12} sm={6} md={6} lg={6}>
          <Card classes={{ root: classes.card }}>
            <CardMedia style={{ backgroundColor: COLORS.RED }}>
              <Typography variant="h5" classes={{ h5: classes.h5 }}>
                Account
                {!!accountPacks && (
                  <>
                    {" "}
                    <span className={classes.titleValue}>[{accountPacks}]</span>
                  </>
                )}
              </Typography>
            </CardMedia>
            <CardContent>
              <TextField
                label="Account Level"
                value={accountLevel}
                onChange={(event) => {
                  if (event.target.value < 0) update("accountLevel", 0);
                  else if (event.target.value > 500)
                    update("accountLevel", 500);
                  else update("accountLevel", parseInt(event.target.value));
                }}
                helperText="The number above your head in the lobby"
                type="number"
                variant="outlined"
                inputProps={{ pattern: "\\d*" }}
                data-cy="account-level"
              />
            </CardContent>
          </Card>
        </Grid>

        {/* purchases */}
        <Grid item xs={12} sm={6} md={6} lg={6}>
          <Card classes={{ root: classes.card }}>
            <CardMedia style={{ backgroundColor: COLORS.MONEY }}>
              <Typography variant="h5" classes={{ h5: classes.h5 }}>
                Purchased Packs
                {!!purchasedPacks && (
                  <>
                    {" "}
                    <span className={classes.titleValue}>
                      [{purchasedPacks}]
                    </span>
                  </>
                )}
              </Typography>
            </CardMedia>
            <CardContent>
              <Typography style={{ display: "block", marginBottom: "1rem" }}>
                How many Apex Packs have you purchased directly from the store?
              </Typography>

              <TextField
                label="Packs Purchased"
                type="number"
                value={purchasedPacks}
                variant="outlined"
                inputProps={{ pattern: "\\d*" }}
                helperText="Check the More Info FAQs for additional&nbsp;information"
                onChange={(event) => {
                  const newVal = parseInt(event.target.value);
                  if (newVal < 0) update("purchasedPacks", 0);
                  else update("purchasedPacks", newVal);
                }}
                data-cy="purchases"
              />
            </CardContent>
          </Card>
        </Grid>

        {SEASONS_ORDERED_REVERSED.map((thisSeason, i) => {
          // hide season 0 preseason
          if (i === SEASONS_ORDERED_REVERSED.length - 1) return null;

          const thisSeasonNumber = SEASONS_ORDERED.length - 1 - i;

          return (
            <React.Fragment key={i}>
              <Grid // [season]
                item
                xs={12}
                sm={6}
                md={6}
                lg={6}
              >
                <Card classes={{ root: classes.card }}>
                  <CardMedia
                    style={{
                      backgroundColor: thisSeason.COLOR,
                      backgroundPosition: "50% 50%",
                      backgroundSize: "100% auto",
                    }}
                    image={thisSeason.TEXTURE}
                  >
                    <Typography
                      variant="h5"
                      classes={{
                        h5: thisSeason.LIGHT_TITLE
                          ? classes.h5
                          : classes.h5_dark,
                      }}
                    >
                      <Typography
                        component="span"
                        style={{ backgroundColor: thisSeason.COLOR }}
                        classes={{
                          root: !!thisSeason.LIGHT_BADGE
                            ? classes.h5prefix
                            : classes.h5prefix_dark,
                        }}
                      >
                        S{SEASONS_ORDERED.length - 1 - i}
                      </Typography>{" "}
                      {thisSeason.NAME}
                      {!!seasonalPacks[thisSeasonNumber] && (
                        <>
                          {" "}
                          <span className={classes.titleValue}>
                            [{seasonalPacks[thisSeasonNumber]}]
                          </span>
                        </>
                      )}
                    </Typography>
                  </CardMedia>
                  <CardContent>
                    <FormControlLabel
                      style={{ display: "block", marginBottom: "1rem" }}
                      control={
                        <Checkbox
                          color="primary"
                          checked={seasonalBattlePasses[thisSeasonNumber]}
                          onChange={() =>
                            update(
                              ["seasonalBattlePasses", thisSeasonNumber],
                              !seasonalBattlePasses[thisSeasonNumber]
                            )
                          }
                          data-cy={`bp-${thisSeasonNumber}`}
                        />
                      }
                      label={
                        <Typography variant="caption">
                          Purchased Battle Pass
                        </Typography>
                      }
                    />

                    <TextField
                      label={`Season ${thisSeasonNumber} Level`}
                      helperText={`Check the Season ${thisSeasonNumber} badge on any Legend`}
                      type="number"
                      value={seasonalAchievedLevel[thisSeasonNumber]}
                      variant="outlined"
                      inputProps={{ pattern: "\\d*" }}
                      onChange={(event) => {
                        const newVal = parseInt(event.target.value);
                        if (newVal < 0)
                          update(
                            ["seasonalAchievedLevel", thisSeasonNumber],
                            0
                          );
                        else if (newVal > 110)
                          update(
                            ["seasonalAchievedLevel", thisSeasonNumber],
                            110
                          );
                        else
                          update(
                            ["seasonalAchievedLevel", thisSeasonNumber],
                            newVal
                          );
                      }}
                      data-cy={`season-${thisSeasonNumber}`}
                    />
                  </CardContent>
                </Card>
              </Grid>

              {thisSeasonNumber >= 5 && ( // [seasonalQuestPacks] start: s5
                <Grid item xs={12} sm={6} md={6} lg={6}>
                  <Card classes={{ root: classes.card }}>
                    <CardMedia
                      style={{
                        backgroundColor:
                          SEASONS_ORDERED[thisSeasonNumber].QUEST_COLOR,
                      }}
                    >
                      <Typography
                        variant="h5"
                        classes={{
                          h5: !!thisSeason.QUEST_LIGHT_TITLE
                            ? classes.h5
                            : classes.h5_dark,
                        }}
                      >
                        <Typography
                          component="span"
                          style={{
                            backgroundColor:
                              SEASONS_ORDERED[thisSeasonNumber].COLOR,
                          }}
                          classes={{
                            root: !!thisSeason.QUEST_LIGHT_BADGE
                              ? classes.h5prefix
                              : classes.h5prefix_dark,
                          }}
                        >
                          S{thisSeasonNumber}
                        </Typography>{" "}
                        {thisSeason.QUEST === "treasurePacks"
                          ? "Treasure Packs"
                          : `Quest: ${thisSeason.QUEST}`}
                        {!!seasonalQuestPacks[thisSeasonNumber] && (
                          <>
                            {" "}
                            <span className={classes.titleValue}>
                              [{seasonalQuestPacks[thisSeasonNumber]}]
                            </span>
                          </>
                        )}
                      </Typography>
                    </CardMedia>
                    <CardContent>
                      <TextField
                        label="Daily Treasure Packs"
                        helperText={`Number of Daily Treasure Packs collected of total ${thisSeason.QUEST_MAX} available`}
                        type="number"
                        value={seasonalTreasurePacks[thisSeasonNumber]}
                        variant="outlined"
                        inputProps={{ pattern: "\\d*" }}
                        onChange={(event) => {
                          const newVal = parseInt(event.target.value);
                          if (newVal < 0)
                            update(
                              ["seasonalTreasurePacks", thisSeasonNumber],
                              0
                            );
                          else if (newVal > thisSeason.QUEST_MAX)
                            update(
                              ["seasonalTreasurePacks", thisSeasonNumber],
                              thisSeason.QUEST_MAX
                            );
                          else
                            update(
                              ["seasonalTreasurePacks", thisSeasonNumber],
                              newVal
                            );
                        }}
                        data-cy={`daily-treasure-packs-s${thisSeasonNumber}`}
                      />
                    </CardContent>
                  </Card>
                </Grid>
              )}

              {!!LTMS[thisSeasonNumber]?.length && // [seasonalLTMs]
                LTMS[thisSeasonNumber]?.map((thisLTM, j) => (
                  <Grid item xs={12} sm={6} md={6} lg={6} key={j}>
                    <Card classes={{ root: classes.card }}>
                      <CardMedia
                        style={{
                          backgroundColor: thisLTM.bgColor || "#787878",
                        }}
                      >
                        <Typography
                          variant="h5"
                          classes={{
                            h5: thisLTM.darkTitle
                              ? classes.h5_dark
                              : classes.h5,
                          }}
                        >
                          <Typography
                            component="span"
                            style={{
                              backgroundColor:
                                SEASONS_ORDERED[thisSeasonNumber].COLOR,
                            }}
                            classes={{
                              root: thisLTM.darkBadge
                                ? classes.h5prefix_dark
                                : classes.h5prefix,
                            }}
                          >
                            S{thisSeasonNumber}
                          </Typography>{" "}
                          {thisLTM.name}
                          {!!seasonalLTMs[thisSeasonNumber][j] && (
                            <>
                              {" "}
                              <span className={classes.titleValue}>
                                [{seasonalLTMs[thisSeasonNumber][j]}]
                              </span>
                            </>
                          )}
                        </Typography>
                      </CardMedia>
                      <CardContent>
                        <TextField
                          label="Event Apex Packs"
                          helperText={`Total number of Apex Packs received out of the ${thisLTM.available} available`}
                          type="number"
                          value={seasonalLTMs[thisSeasonNumber][j]}
                          variant="outlined"
                          inputProps={{ pattern: "\\d*" }}
                          onChange={(event) => {
                            const newVal = parseInt(event.target.value);
                            if (newVal < 0)
                              update(["seasonalLTMs", thisSeasonNumber, j], 0);
                            else if (newVal > thisLTM.available)
                              update(
                                ["seasonalLTMs", thisSeasonNumber, j],
                                thisLTM.available
                              );
                            else
                              update(
                                ["seasonalLTMs", thisSeasonNumber, j],
                                newVal
                              );
                          }}
                          data-cy="s10-ltm"
                        />
                      </CardContent>
                    </Card>
                  </Grid>
                ))}
            </React.Fragment>
          );
        })}

        {/* twitch prime */}
        <Grid item xs={12} sm={6} md={6} lg={6}>
          <Card classes={{ root: classes.card }}>
            <CardMedia style={{ backgroundColor: COLORS.TWITCH }}>
              <Typography variant="h5" classes={{ h5: classes.h5 }}>
                Twitch Prime
                {!!twitchPrime && (
                  <>
                    {" "}
                    <span className={classes.titleValue}>[5]</span>
                  </>
                )}
              </Typography>
            </CardMedia>
            <CardContent>
              <FormControlLabel
                control={
                  <Checkbox
                    color="primary"
                    checked={twitchPrime}
                    onChange={() => update("twitchPrime", !twitchPrime)}
                    data-cy="twitch-prime"
                  />
                }
                label={
                  <Typography variant="caption">Linked Twitch Prime</Typography>
                }
              />
            </CardContent>
          </Card>
        </Grid>

        {/* misc */}
        <Grid item xs={12} sm={6} md={6} lg={6}>
          <Card classes={{ root: classes.card }}>
            <CardMedia style={{ backgroundColor: COLORS.MISC }}>
              <Typography variant="h5" classes={{ h5: classes.h5 }}>
                Bugs + Misc Packs
                {!!miscPacks && (
                  <>
                    {" "}
                    <span className={classes.titleValue}>[{miscPacks}]</span>
                  </>
                )}
              </Typography>
            </CardMedia>
            <CardContent>
              <Typography paragraph={true}>
                There have been{" "}
                <Link
                  href="https://www.reddit.com/r/apexlegends/comments/e70q5l/how_many_packs_was_i_supposed_to_get/"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  reports
                </Link>{" "}
                of players receiving more Apex Packs than they should have due
                to a bug.
              </Typography>

              <Typography variant="caption" paragraph={true}>
                (The answer for most players is <code>0</code>)
              </Typography>

              <TextField
                label="Misc Packs"
                style={{ maxWidth: 320 }}
                type="number"
                value={miscPacks}
                variant="outlined"
                inputProps={{ pattern: "\\d*" }}
                onChange={(event) => {
                  const newVal = parseInt(event.target.value);
                  if (newVal < 0) update("miscPacks", 0);
                  else update("miscPacks", newVal);
                }}
                data-cy="misc"
              />
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      <Grid
        container
        spacing={2}
        style={{ marginTop: 24, marginBottom: 48 }}
        className="width-constrained"
      >
        <Grid item>
          <Button
            onClick={() => {
              window.scrollTo(0, 0);
              setActiveTab(0);

              window.gtag("event", "click", {
                event_category: "Form Button",
                event_action: "View Progress Button",
              });
            }}
            variant="contained"
            color="primary"
            data-cy="button-progress"
          >
            View Progress
          </Button>
        </Grid>

        <Grid item>
          <Button
            onClick={() => {
              resetAll();

              window.gtag("event", "click", {
                event_category: "Form Button",
                event_action: "Reset Button",
              });
            }}
            variant="outlined"
            color="primary"
            data-cy="button-reset"
          >
            Reset
          </Button>
        </Grid>
      </Grid>
    </>
  );
};
